import { useEffect, useRef, useState } from "react";
import { testimonialsData } from "@/mocks/home";

export default function Testimonials() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasAnimated(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const animatedClass = hasAnimated ? "animate-fade-in-up" : "opacity-0";

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="relative py-20 md:py-28 section-padding overflow-hidden bg-[#f8f9fb]"
    >
      <div className="absolute top-0 left-0 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

      <div className="container-wide relative z-10">
        <div className="text-center mb-14 md:mb-20 max-w-2xl mx-auto">
          <div className={animatedClass}>
            <span className="inline-block px-4 py-1.5 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-600 text-xs md:text-sm font-semibold tracking-wide mb-4">
              Testimonials
            </span>
          </div>
          <h2
            className={`${animatedClass} font-outfit font-bold text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4`}
          >
            Trusted by Developers
          </h2>
          <p
            className={`${animatedClass} text-base md:text-lg text-gray-500 leading-relaxed`}
          >
            See what teams and developers say about working with <strong className="text-gray-700">SaaS ForceKeys Services</strong> and using <strong className="text-gray-700">Translate Cloud API</strong> for their <strong className="text-gray-700">translation API</strong> needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {testimonialsData.map((t, i) => (
            <div
              key={t.id}
              className={`${animatedClass} p-6 md:p-8 rounded-2xl bg-white border border-gray-100 hover:shadow-lg hover:shadow-emerald-500/5 hover:border-emerald-100 transition-all duration-500 ${
                i === activeIndex
                  ? "shadow-lg shadow-emerald-500/5"
                  : ""
              }`}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-outfit font-semibold text-gray-900 text-sm md:text-base">
                    {t.name}
                  </h4>
                  <p className="text-xs md:text-sm text-gray-500">{t.role}</p>
                </div>
              </div>

              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, starIdx) => (
                  <div
                    key={starIdx}
                    className="w-4 h-4 flex items-center justify-center text-amber-400"
                  >
                    <i className="ri-star-fill text-sm" />
                  </div>
                ))}
              </div>

              <p className="text-sm md:text-base text-gray-500 leading-relaxed italic">
                &ldquo;{t.content}&rdquo;
              </p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 mt-10 md:mt-12">
          {testimonialsData.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                i === activeIndex
                  ? "bg-emerald-500 w-8"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}